import { createContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { subscribeToSessionIdleExpired } from '@/lib/sessionExpirySignal';
/**
 * AuthContext
 *
 * 真实身份与会话状态由后端 (`apps/api`) 通过 OpenAPI typed client (`@jinzhuan/api-client`)
 * 提供。在该 typed client 落地之前，本 Context 只会通过 `__dev__/mocks/devAuthAdapter`
 * 的 dev-only 适配器获取占位会话，且仅在 `import.meta.env.DEV === true` 时启用。
 *
 * 设计要点：
 * - Context 不内置任何权限语义；`hasPermission` / `hasRole` 字段不在前端实现，
 *   待后端下发真实权限矩阵后再统一接入，避免前端伪装权限系统。
 * - 生产构建 (`import.meta.env.PROD`) 一旦未注入真实 adapter，调用 `signIn`
 *   会立即抛错，拒绝静默退化。
 *
 * Issue #230 transient-failure contract:
 * - `readSession()` returns a three-state result. `unauthenticated` clears any
 *   stored session; `transient_failure` (5xx, network, malformed upstream)
 *   leaves the existing session intact so an authenticated admin is not
 *   redirected to /login while the backend flaps.
 */
export interface AuthSession {
  readonly subject: string;
  readonly userId: string;
  readonly tenantId: string;
  readonly issuedAt: string;
  readonly permissions: string[];
  readonly roles: string[];
  /** 来源标识：dev-mock / api-client，用于 UI 显示警示。 */
  readonly source: 'dev-mock' | 'api-client';
}

/** signIn 输入参数。tenantSlug 为必填，第一版预填为单租户标识，保持可编辑。 */
export interface SignInInput {
  /** 机构标识（Organization.slug）。必填，第一版 LoginPage 预填单租户标识。 */
  readonly tenantSlug: string;
  readonly email: string;
  readonly password: string;
}

/**
 * Discriminated result of `AuthAdapter.readSession`.
 *
 * - `authenticated`: backend returned a valid session.
 * - `unauthenticated`: backend explicitly rejected the call (401 / 403).
 * - `transient_failure`: backend was unreachable, returned 5xx, returned a
 *   non-JSON / malformed body, or the network errored. The caller MUST NOT
 *   clear the local access token on this state.
 */
export type ReadSessionResult =
  | { readonly status: 'authenticated'; readonly session: AuthSession }
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'transient_failure'; readonly reason: string };

export interface AuthContextValue {
  session: AuthSession | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  /**
   * True when the last `readSession()` attempt during initialization (or a
   * subsequent refresh) returned a transient failure AND no prior session was
   * available. Consumers can show a "service temporarily unavailable" panel
   * with a retry CTA instead of redirecting to /login.
   */
  transientFailure: boolean;
  /** Last transient failure reason code (sanitized — no driver details). */
  transientFailureReason: string | null;
  userId: string | null;
  tenantId: string | null;
  permissions: string[];
  roles: string[];
  signIn: (input: SignInInput) => Promise<void>;
  signOut: () => void;
  /**
   * Re-attempt `readSession()` on demand (e.g. user clicks "retry" after a
   * transient failure). Resolves once the next state has been applied.
   */
  refreshSession: () => Promise<void>;
}
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);
export interface AuthProviderProps {
  children: ReactNode;
  /**
   * 真实环境下由 packages/api-client 注入的会话适配器；缺省时仅在 DEV 下回落到
   * `__dev__/mocks/devAuthAdapter`。
   */
  adapter?: AuthAdapter;
  /**
   * Override the readSession retry policy. Production default: 3 attempts with
   * exponential backoff to ride out brief LB / RDS flaps before showing the
   * transient-failure UI.
   */
  initialReadRetry?: {
    readonly maxAttempts: number;
    readonly initialDelayMs: number;
  };
}
export interface AuthAdapter {
  readSession: () => Promise<ReadSessionResult>;
  signIn: (input: SignInInput) => Promise<AuthSession>;
  signOut: () => Promise<void>;
}

const DEFAULT_INITIAL_READ_RETRY = { maxAttempts: 3, initialDelayMs: 250 } as const;

export function AuthProvider({ children, adapter, initialReadRetry }: AuthProviderProps) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [transientFailure, setTransientFailure] = useState(false);
  const [transientFailureReason, setTransientFailureReason] = useState<string | null>(null);
  const sessionRef = useRef<AuthSession | null>(null);
  const retryPolicy = initialReadRetry ?? DEFAULT_INITIAL_READ_RETRY;

  const resolvedAdapter = useMemo<AuthAdapter>(() => {
    if (adapter) {
      return adapter;
    }
    if (import.meta.env.DEV) {
      return createDevAuthAdapter();
    }
    return createUnconfiguredAdapter();
  }, [adapter]);

  const applyResult = (result: ReadSessionResult): void => {
    if (result.status === 'authenticated') {
      sessionRef.current = result.session;
      setSession(result.session);
      setTransientFailure(false);
      setTransientFailureReason(null);
      return;
    }
    if (result.status === 'unauthenticated') {
      sessionRef.current = null;
      setSession(null);
      setTransientFailure(false);
      setTransientFailureReason(null);
      return;
    }
    // transient_failure — preserve any previous session, surface the flag.
    setTransientFailure(true);
    setTransientFailureReason(result.reason);
  };

  const attemptReadSession = async (): Promise<ReadSessionResult> => {
    let lastResult: ReadSessionResult = { status: 'transient_failure', reason: 'not_attempted' };
    for (let attempt = 1; attempt <= retryPolicy.maxAttempts; attempt++) {
      lastResult = await resolvedAdapter.readSession();
      if (lastResult.status !== 'transient_failure') {
        return lastResult;
      }
      if (attempt < retryPolicy.maxAttempts) {
        await sleep(retryPolicy.initialDelayMs * 2 ** (attempt - 1));
      }
    }
    return lastResult;
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await attemptReadSession();
        if (!cancelled) {
          applyResult(result);
        }
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedAdapter]);

  // Issue #289: centralized forced-logout on backend-reported idle expiration.
  // Any authenticated API hook using `createSessionAwareApiClient` (or any
  // `readSession()` 401 classified as idle-expired) fires
  // `sessionExpirySignal`. We react by clearing local auth state and asking
  // the adapter to drop its server-side token — `ProtectedShell` then sees
  // `isAuthenticated === false` and `Redirect`s the user to `/login`.
  useEffect(() => {
    const unsubscribe = subscribeToSessionIdleExpired(() => {
      sessionRef.current = null;
      setSession(null);
      setTransientFailure(false);
      setTransientFailureReason(null);
      void resolvedAdapter.signOut().catch(() => undefined);
    });
    return unsubscribe;
  }, [resolvedAdapter]);

  const value: AuthContextValue = {
    session,
    isAuthenticated: session !== null,
    isInitializing,
    transientFailure,
    transientFailureReason,
    userId: session?.userId ?? null,
    tenantId: session?.tenantId ?? null,
    permissions: session?.permissions ?? [],
    roles: session?.roles ?? [],
    signIn: async (input) => {
      const next = await resolvedAdapter.signIn(input);
      sessionRef.current = next;
      setSession(next);
      setTransientFailure(false);
      setTransientFailureReason(null);
    },
    signOut: () => {
      void resolvedAdapter.signOut();
      sessionRef.current = null;
      setSession(null);
      setTransientFailure(false);
      setTransientFailureReason(null);
    },
    refreshSession: async () => {
      const result = await attemptReadSession();
      applyResult(result);
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
/* ------------------------------------------------------------------ */
/* Adapter factories                                                  */
/* ------------------------------------------------------------------ */
function createDevAuthAdapter(): AuthAdapter {
  // 仅在 import.meta.env.DEV 分支中被调用，在 prod 构建中由 Vite tree-shaking
  // 整体剔除（`createDevAuthAdapter` 本身也不会被引用）。
  /* v8 ignore start */
  if (!import.meta.env.DEV) {
    throw new Error('createDevAuthAdapter must not be invoked in production builds');
  }
  /* v8 ignore stop */
  return {
    readSession: async () => {
      const mod = await import('@/__dev__/mocks/devAuthAdapter');
      const dev = mod.__devReadSession();
      return dev
        ? { status: 'authenticated', session: toAuthSession(dev) }
        : { status: 'unauthenticated' };
    },
    signIn: async (input) => {
      const mod = await import('@/__dev__/mocks/devAuthAdapter');
      const dev = mod.__devSignIn(input);
      return toAuthSession(dev);
    },
    signOut: async () => {
      const mod = await import('@/__dev__/mocks/devAuthAdapter');
      mod.__devSignOut();
    },
  };
}
function createUnconfiguredAdapter(): AuthAdapter {
  const fail = () => {
    throw new Error(
      '[AuthContext] No production AuthAdapter injected. ' +
        'Wire @jinzhuan/api-client based adapter via <AuthProvider adapter={...}> before deploying.',
    );
  };
  return {
    readSession: async () => ({ status: 'unauthenticated' }),
    signIn: async () => fail(),
    signOut: async () => fail(),
  };
}
function toAuthSession(dev: {
  subject: string;
  userId: string;
  tenantId: string;
  issuedAt: string;
  permissions?: string[];
  roles?: string[];
}): AuthSession {
  return {
    subject: dev.subject,
    userId: dev.userId,
    tenantId: dev.tenantId,
    issuedAt: dev.issuedAt,
    permissions: dev.permissions ?? [],
    roles: dev.roles ?? [],
    source: 'dev-mock',
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
